import { useEffect, useState } from 'react';
import { api } from '../api.ts';

/**
 * UpgradeBanner — in-app notification when Cloudflare resource quotas
 * approach saturation. NO email. Mount once in App.tsx; auto-polls every
 * 5 minutes. Two visual states:
 *
 *   warning (kuning) → ≥75% of any quota
 *   critical (merah) → ≥92%, OR upgradeNeeded=true from server
 *
 * Click → buka Cloudflare dashboard di tab baru. Setelah Tico upgrade
 * Workers plan, kuota auto-naik (Browser Rendering 6000 → 100.000 menit,
 * dll) — banner hilang otomatis pada poll berikutnya tanpa reconfig
 * apapun di kode kita.
 */

interface UsageSummary {
  warnings: string[];
  upgradeNeeded: boolean;
  limits: {
    browserMinutes: { limit: number; used: number; pct: number };
    anthropicRPM: { limit: number; used: number; pct: number };
    r2GB: { limit: number; used: number; pct: number };
  };
}

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const UPGRADE_URL = 'https://dash.cloudflare.com/?to=/:account/workers/plans';

export function UpgradeBanner() {
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await api.usageSummary();
        if (!cancelled) setSummary(res);
      } catch {
        /* non-fatal — banner stays hidden on error */
      }
    };
    void fetchOnce();
    const id = window.setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (dismissed || !summary || summary.warnings.length === 0) return null;

  const isCritical =
    summary.upgradeNeeded ||
    summary.limits.browserMinutes.pct >= 0.92 ||
    summary.limits.anthropicRPM.pct >= 0.95 ||
    summary.limits.r2GB.pct >= 0.95;

  return (
    <aside
      className={`upgrade-banner upgrade-banner--${isCritical ? 'critical' : 'warning'}`}
      role={isCritical ? 'alert' : 'status'}
      aria-live={isCritical ? 'assertive' : 'polite'}
    >
      <div className="upgrade-banner__inner">
        <span className="upgrade-banner__dot" aria-hidden="true" />
        <div className="upgrade-banner__body">
          <strong className="upgrade-banner__title">
            {isCritical ? 'Upgrade Worker direkomendasikan' : 'Kuota mendekati limit'}
          </strong>
          <span className="upgrade-banner__msg">{summary.warnings[0]}</span>
          {summary.warnings.length > 1 ? (
            <a href="#/usage" className="upgrade-banner__more">
              +{summary.warnings.length - 1} peringatan lain di Halaman Usage
            </a>
          ) : null}
        </div>
        <div className="upgrade-banner__actions">
          {isCritical ? (
            <a
              href={UPGRADE_URL}
              target="_blank"
              rel="noopener"
              className="upgrade-banner__cta"
            >
              Upgrade
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
                <path
                  d="M14 4h6v6M20 4 10 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </a>
          ) : (
            <a href="#/usage" className="upgrade-banner__cta upgrade-banner__cta--ghost">
              Detail
            </a>
          )}
          <button
            type="button"
            className="upgrade-banner__close"
            onClick={() => setDismissed(true)}
            aria-label="Tutup notifikasi"
            title="Tutup"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M6 18l12-12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}
